/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { default as AutoSizer } from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { TokenSelectionStore } from 'src/luis/modules/labeling/utils/TokenSelectionStore';
import { Line, LineProps, TokenUIProps } from 'src/modules/labeling/components/Line';
import { Label, MultilineLabel } from 'src/modules/labeling/types/Label';
import { ShapeUIProps } from 'src/modules/labeling/types/ShapeUIProps';
import { ViewOptions } from 'src/modules/labeling/types/ViewOptions';
import { tokenContainerDataAttribute } from 'src/modules/labeling/utils/labelingConstants';
import { convertToMultiline } from 'src/modules/labeling/utils/layout';

const { styleSpan } = getStylistV2('LayoutManager');

const Container = styleSpan('Container', theme => ({
    display: 'flex',
    position: 'relative',
    flex: 1,
    fontFamily: theme.textFontFamily,
    fontSize: theme.textFontSize,
    userSelect: 'none'
}));

const TokensContainer = styleSpan(
    'TokensContainer',
    (_, cssVars) => ({
        position: 'relative',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        $nest: {
            '&:hover': {
                cursor: cssVars.cursor
            }
        }
    }),
    {
        cssVars: {
            cursor: ''
        },
        cssVarsSetter: ({ disabled }: { disabled: boolean }) => ({
            cursor: disabled ? 'default' : 'text'
        })
    }
);

export type LayoutManagerProps = {
    /**
     * Array of utterance tokens.
     */
    linesTokens: string[][];

    /**
     * Array of labels.
     */
    labels: Label[];

    /**
     * Boolean indicating whether an error indicator icon should be displayed or not.
     */
    hasConflict: boolean;

    /**
     * Labeling view options.
     */
    viewOptions: ViewOptions;

    /**
     * Whether not-allowed cursor should render or not.
     */
    disabled: boolean;

    /**
     * Whether the utterance is RTL or not.
     */
    isRTL: boolean;

    /**
     * The store controlling token selection.
     */
    tokenSelectionStore: TokenSelectionStore;

    /**
     * Reference to the html element housing the tokens.
     */
    tokensContainerRef: React.MutableRefObject<HTMLElement>;

    /**
     * Aria label to be added on the dom element.
     */
    'aria-label': string;

    /**
     * Boolean indicating whether there is a label being resized.
     */
    isResizing: boolean;

    /**
     * Unique identifier for the tokens container.
     */
    containerId: string;

    /**
     * Virtualizes labelers lines.
     */
    useVirtualization: boolean;

    /**
     * CSS style object to get applied to the root element.
     */
    style?: React.CSSProperties;

    /**
     * CSS class to get applied to the root element.
     */
    className?: string;

    /**
     * Callback function that takes in a shape ui object and returns a shape component
     * to be rendered by the LayoutManager.
     */
    onShapeRender: (shapeProps: ShapeUIProps, hidden: boolean) => React.ReactNode;

    /**
     * Callback function that takes in a token ui object and returns a token component
     * to be rendered by the LayoutManager.
     */
    onTokenRender: (tokenProps: TokenUIProps, absoluteTokenIndex: number) => React.ReactNode;
};

const getLineIndices = (linesTokens: string[][]) => {
    const indices: [number, number][] = [];

    let countSoFar = 0;
    for (const line of linesTokens) {
        indices.push([countSoFar, countSoFar + line.length - 1]);

        countSoFar += line.length;
    }

    return indices;
};

const ESTIMATED_LINE_HEIGHT = 36;
const LIST_OVERSCAN = 10;

type ListData = Omit<LineProps, 'lineIndex' | 'style' | 'tokens' | 'labels' | 'absoluteStartTokenIndex'> & {
    linesLabels: MultilineLabel[][];
    linesIndices: [number, number][];
    linesTokens: string[][];
};

/**
 * The reason why we use this weird indirection, is because react-window
 * needs to stable reference for the rowRenderer callback, otherwise, it
 * will re-mount all the rows again which results in a UI flicker.
 */
const lineRenderer = ({ index: lineIndex, data, style }: { index: number; style?: React.CSSProperties; data: ListData }) => {
    const {
        hasConflict,
        isRTL,
        linesIndices,
        linesLabels,
        onCalculateHeight,
        onShapeRender,
        onTokenRender,
        viewOptions,
        linesTokens,
        tokenSelectionStore
    } = data;

    const lineElement = (
        <Line
            isRTL={isRTL}
            key={lineIndex}
            lineIndex={lineIndex}
            hasConflict={hasConflict}
            viewOptions={viewOptions}
            onShapeRender={onShapeRender}
            onTokenRender={onTokenRender}
            tokens={linesTokens[lineIndex]}
            labels={linesLabels[lineIndex]}
            onCalculateHeight={onCalculateHeight}
            tokenSelectionStore={tokenSelectionStore}
            absoluteStartTokenIndex={linesIndices[lineIndex][0]}
        />
    );

    return style ? (
        <div key={lineIndex} style={style}>
            {lineElement}
        </div>
    ) : (
        lineElement
    );
};

/**
 * LayoutManager component responsible for rendering shapes based on the given labels and tokens based on
 * the tokenizedText and selection and conflict states.
 */
export const LayoutManager = React.memo(
    React.forwardRef(<T extends HTMLElement>(props: LayoutManagerProps, ref: React.MutableRefObject<T>) => {
        const {
            labels,
            linesTokens,
            hasConflict,
            onShapeRender,
            onTokenRender,
            viewOptions,
            disabled,
            isRTL,
            tokensContainerRef,
            'aria-label': ariaLabel,
            isResizing: _,
            containerId,
            tokenSelectionStore,
            useVirtualization,
            ...rest
        } = props;

        const lineHeights = React.useRef<{ [index: number]: number }>({});
        const listRef = React.useRef<List>();
        const linesIndices = React.useMemo(() => getLineIndices(linesTokens), [linesTokens]);
        const linesLabels = React.useMemo(() => convertToMultiline(labels, linesIndices), [labels, linesIndices]);
        const [setWidth, setSetWidth] = React.useState<number>();

        const handleCalculateLineHeight = React.useCallback((lineIndex: number, height: number) => {
            lineHeights.current[lineIndex] = height;
            if (listRef.current) {
                listRef.current.resetAfterIndex(lineIndex, true);
            }
        }, []);

        const setInitialWidth = React.useCallback(() => {
            setSetWidth(width => (!width ? tokensContainerRef.current.getBoundingClientRect().width : width));
        }, [setSetWidth, tokensContainerRef]);

        const listData: ListData = {
            hasConflict,
            isRTL,
            linesIndices,
            linesLabels,
            linesTokens,
            onShapeRender,
            onTokenRender,
            viewOptions,
            tokenSelectionStore,
            onCalculateHeight: handleCalculateLineHeight
        };

        return (
            <Container aria-label={ariaLabel} originalRef={ref} {...rest}>
                <TokensContainer
                    {...{ [tokenContainerDataAttribute]: containerId }}
                    customProps={{ disabled }}
                    originalRef={tokensContainerRef}>
                    {useVirtualization ? (
                        <AutoSizer nonce={window.cspNonce}>
                            {({ width, height }: { width: number; height: number }) => (
                                <List
                                    ref={listRef}
                                    width={setWidth ?? width}
                                    height={height}
                                    itemData={listData}
                                    itemCount={linesTokens.length}
                                    itemSize={index => lineHeights.current[index] ?? ESTIMATED_LINE_HEIGHT}
                                    estimatedItemSize={ESTIMATED_LINE_HEIGHT}
                                    overscanCount={LIST_OVERSCAN}
                                    onItemsRendered={setInitialWidth}>
                                    {lineRenderer}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        linesTokens.map((__, index) => lineRenderer({ index, data: listData }))
                    )}
                </TokensContainer>
            </Container>
        );
    })
);

LayoutManager.displayName = 'LayoutManager';
